import { COUNTRY_CODES } from "@redotech/locale/countries";
import { CURRENCIES, Currency } from "@redotech/money/currencies";
import { WeightUnit as CrdbWeightUnit } from "@redotech/redo-prisma-client/enums";
import { assertNever } from "@redotech/util/type";
import * as z from "zod/v4";
import { createEnum } from "../common/zod-util";

export enum LengthUnit {
  INCH = "in",
  CENTIMETER = "cm",
}
export const LengthUnitSchema = z.enum(LengthUnit);

export function lengthUnitDisplayName(unit: LengthUnit): string {
  switch (unit) {
    case LengthUnit.INCH:
      return "inches";
    case LengthUnit.CENTIMETER:
      return "centimeters";
    default:
      return unit;
  }
}

export enum Precision {
  AT_LEAST = "at-least",
  EXACT = "exact",
}
export const PrecisionSchema = z.enum(Precision);

export const CountryCodeSchema = z
  .string()
  .refine((code: any) => COUNTRY_CODES.includes(code), {
    message: "countryCode must be found in COUNTRY_CODES",
  });

export const CurrencySchema = z.custom<Currency>(
  (data: any) => CURRENCIES.includes(data),
  { message: "currencyCode must be found in CURRENCIES" },
);

export const MoneySchema = z.object({
  amount: z.string(),
  currency: createEnum(Currency),
});

export type IMoney = z.infer<typeof MoneySchema>;

export enum WeightUnit {
  GRAM = "g",
  KILOGRAM = "kg",
  OUNCE = "oz",
  POUND = "lb",
  TONNES = "t",
}
export const WeightUnitSchema = z
  .custom<WeightUnit>((data: any) => !!weightTransforms[data], {
    message: "Unknown weight unit",
  })
  .transform((arg) => weightTransforms[arg]!);

export const WeightSchema = z.object({
  unit: WeightUnitSchema,
  value: z.number(),
});

export type Weight = z.infer<typeof WeightSchema>;

export interface IWeight extends z.ZodType<Weight> {}

const weightTransforms: Record<string, WeightUnit> = {
  GRAMS: WeightUnit.GRAM,
  KILOGRAMS: WeightUnit.KILOGRAM,
  OUNCES: WeightUnit.OUNCE,
  POUNDS: WeightUnit.POUND,
  TONNES: WeightUnit.TONNES,
  g: WeightUnit.GRAM,
  kg: WeightUnit.KILOGRAM,
  lb: WeightUnit.POUND,
  lbs: WeightUnit.POUND,
  oz: WeightUnit.OUNCE,
  t: WeightUnit.TONNES,
  G: WeightUnit.GRAM,
  KG: WeightUnit.KILOGRAM,
  LB: WeightUnit.POUND,
  LBS: WeightUnit.POUND,
  OZ: WeightUnit.OUNCE,
  T: WeightUnit.TONNES,
};

const weightUnitConversion: Record<WeightUnit, Record<WeightUnit, number>> = {
  [WeightUnit.OUNCE]: {
    [WeightUnit.POUND]: 0.0625,
    [WeightUnit.OUNCE]: 1,
    [WeightUnit.GRAM]: 28.349_523_125,
    [WeightUnit.KILOGRAM]: 0.028_349_523_1,
    [WeightUnit.TONNES]: 0.000_028_349_5,
  },
  [WeightUnit.POUND]: {
    [WeightUnit.OUNCE]: 16,
    [WeightUnit.POUND]: 1,
    [WeightUnit.GRAM]: 453.592_37,
    [WeightUnit.KILOGRAM]: 0.453_592_37,
    [WeightUnit.TONNES]: 0.000_453_592_4,
  },
  [WeightUnit.GRAM]: {
    [WeightUnit.OUNCE]: 0.035_273_961_9,
    [WeightUnit.POUND]: 0.002_204_622_6,
    [WeightUnit.GRAM]: 1,
    [WeightUnit.KILOGRAM]: 0.001,
    [WeightUnit.TONNES]: 0.001,
  },
  [WeightUnit.KILOGRAM]: {
    [WeightUnit.OUNCE]: 35.273_961_949_6,
    [WeightUnit.POUND]: 2.204_622_621_8,
    [WeightUnit.GRAM]: 1000,
    [WeightUnit.KILOGRAM]: 1,
    [WeightUnit.TONNES]: 0.001,
  },
  [WeightUnit.TONNES]: {
    [WeightUnit.OUNCE]: 35_273.961_95,
    [WeightUnit.POUND]: 2204.622_621_8,
    [WeightUnit.GRAM]: 1_000_000,
    [WeightUnit.KILOGRAM]: 1000,
    [WeightUnit.TONNES]: 1,
  },
};

export function convertWeight(weight: Weight, to: WeightUnit): Weight {
  const converted = weightUnitConversion[weight.unit][to] * weight.value;
  return { unit: to, value: roundIfVeryClose(converted) };
}

function roundIfVeryClose(value: number): number {
  const rounded = Math.round(value);
  if (Math.abs(value - rounded) < 0.001) {
    return rounded;
  }
  return value;
}

export const roundWeight = (weight: number) => {
  return Math.round(weight * 100) / 100;
};

export const splitWeight = (
  weight: Weight,
  units: [WeightUnit, WeightUnit],
): [number, number] => {
  const firstPart = convertWeight(weight, units[0]);
  let firstWhole = Math.floor(firstPart.value);
  const secondPart = convertWeight(
    { value: firstPart.value - firstWhole, unit: units[0] },
    units[1],
  );
  let secondWhole = Math.ceil(secondPart.value);
  // Due to rounding errors, rounding up the second unit
  // may make it a whole number of the first unit. (1 lb 16 oz)
  // Account for that here. (2 lb 0 oz)
  if (
    convertWeight({ value: secondWhole, unit: units[1] }, units[0]).value === 1
  ) {
    firstWhole++;
    secondWhole = 0;
  }
  return [firstWhole, secondWhole];
};

export function addWeight(weight: Weight, weightToAdd: Weight): Weight {
  const convertedWeightToAdd = convertWeight(weightToAdd, weight.unit);
  return {
    unit: weight.unit,
    value: weight.value + convertedWeightToAdd.value,
  };
}

export function weightsEqual(
  weight1: Weight | undefined | null,
  weight2: Weight | undefined | null,
): boolean {
  // If both weights are undefined or null, they are equal
  if (!weight1 && !weight2) {
    return true;
  }
  // If we have one weight and the other is undefined or null, they are not equal
  if (!weight1 || !weight2) {
    return false;
  }
  // Otherwise convert to grams and compare
  const convertedWeight1 = convertWeight(weight1, WeightUnit.GRAM);
  const convertedWeight2 = convertWeight(weight2, WeightUnit.GRAM);
  return convertedWeight1.value === convertedWeight2.value;
}

export enum WeightFormat {
  IMPERIAL = "imperial",
  METRIC = "metric",
}

export const crdbWeightUnitToWeightUnit = (
  crdbUnit: CrdbWeightUnit,
): WeightUnit => {
  switch (crdbUnit) {
    case CrdbWeightUnit.Gram:
      return WeightUnit.GRAM;
    case CrdbWeightUnit.Pound:
      return WeightUnit.POUND;
    case CrdbWeightUnit.Ounce:
      return WeightUnit.OUNCE;
    case CrdbWeightUnit.Kilogram:
      return WeightUnit.KILOGRAM;
    case CrdbWeightUnit.Tonne:
      return WeightUnit.TONNES;
    default:
      assertNever(crdbUnit);
  }
};
